/**
 * MFASetup — Section "Authentification à 2 facteurs" dans le profil utilisateur.
 *
 * Utilise exclusivement les fonctions Supabase MFA :
 *   - supabase.auth.mfa.enroll()    → récupère QR code (SVG natif)
 *   - supabase.auth.mfa.challenge() → crée un challenge TOTP
 *   - supabase.auth.mfa.verify()    → valide le code 6 chiffres
 *   - supabase.auth.mfa.unenroll()  → désactive le MFA
 *
 * Pas de dépendance externe (le QR code est livré en SVG par Supabase).
 */
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Shield, ShieldCheck, ShieldOff, QrCode } from 'lucide-react';

export default function MFASetup() {
  // 'idle' | 'enroll' | 'active'
  const [step, setStep] = useState('idle');
  const [factors, setFactors] = useState([]);

  // Données d'enrollment
  const [qrSvg, setQrSvg] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [challengeId, setChallengeId] = useState('');

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Charger les facteurs existants au montage
  useEffect(() => {
    loadFactors();
  }, []);

  const loadFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) return;
      const totp = data?.totp || [];
      setFactors(totp);
      if (totp.some(f => f.status === 'verified')) {
        setStep('active');
      } else {
        setStep('idle');
      }
    } catch {
      // Silencieux si l'utilisateur n'a pas de session Supabase active
    }
  };

  // Helper : récupérer tous les facteurs TOTP (dédupliqués)
  const getAllTotpFactors = async () => {
    const { data } = await supabase.auth.mfa.listFactors();
    const all = [
      ...(data?.totp || []),
      ...(data?.all?.filter(f => f.factor_type === 'totp') || []),
    ];
    return [...new Map(all.map(f => [f.id, f])).values()];
  };

  // ─── Étape 1 : Enrollment —————————————————————————————————
  const handleEnroll = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Supprimer tous les facteurs TOTP existants
      const existing = await getAllTotpFactors();
      for (const f of existing) {
        try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch {}
      }

      // 2. Poller jusqu'à ce que la liste soit vraiment vide (max 15 × 500ms = 7.5s)
      if (existing.length > 0) {
        for (let i = 0; i < 15; i++) {
          await new Promise(r => setTimeout(r, 500));
          const remaining = await getAllTotpFactors();
          if (remaining.length === 0) break;
          // Si encore des facteurs au dernier essai, les ré-unenroller
          if (i === 14) {
            for (const f of remaining) {
              try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch {}
            }
            await new Promise(r => setTimeout(r, 1000));
          }
        }
      }

      // 3. Enroll avec un nom unique pour éviter tout conflit de nom
      const friendlyName = `skyapp-${Date.now()}`;
      const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName,
      });
      if (enrollErr) throw enrollErr;

      // qr_code est déjà une data URL ou SVG brut — on stocke tel quel
      setQrSvg(data.totp.qr_code || '');
      setSecret(data.totp.secret);
      setFactorId(data.id);

      // Créer un challenge immédiatement
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: data.id });
      if (challengeErr) throw challengeErr;
      setChallengeId(challenge.id);

      setStep('enroll');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'activation MFA');
    } finally {
      setLoading(false);
    }
  };

  // ─── Étape 2 : Vérification du code ──────────────────────────
  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });
      if (verifyErr) throw verifyErr;

      toast.success('Authentification à 2 facteurs activée !');
      setCode('');
      await loadFactors();
    } catch (err) {
      setError('Code invalide ou expiré. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Désactivation ────────────────────────────────────────────
  const handleDisable = async () => {
    setLoading(true);
    try {
      const allFactors = await getAllTotpFactors();
      for (const f of allFactors) {
        try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch {}
      }
      toast.success('MFA désactivé');
      setStep('idle');
      setFactors([]);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la désactivation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3 mt-4">
      {/* En-tête */}
      <div className="flex items-center gap-2">
        {step === 'active'
          ? <ShieldCheck className="w-5 h-5 text-green-600" />
          : <Shield className="w-5 h-5 text-gray-400" />
        }
        <h3 className="font-semibold text-sm">Authentification à 2 facteurs (MFA)</h3>
        {step === 'active' && (
          <span className="ml-auto text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            Activé
          </span>
        )}
      </div>

      {/* ── Étape : inactif ── */}
      {step === 'idle' && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            Protégez votre compte avec une application d'authentification
            (Google Authenticator, Authy…).
          </p>
          <Button
            onClick={handleEnroll}
            disabled={loading}
            size="sm"
            variant="outline"
            className="w-full"
          >
            <QrCode className="w-4 h-4 mr-2" />
            {loading ? 'Chargement...' : 'Activer le MFA'}
          </Button>
        </div>
      )}

      {/* ── Étape : scan QR ── */}
      {step === 'enroll' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Scannez ce QR code avec votre application d'authentification :
          </p>

          {/* QR code — rendu via <img> quelle que soit la forme (data URL ou SVG) */}
          <div className="flex justify-center p-4 bg-white border-2 border-gray-200 rounded-xl">
            <img
              src={qrSvg.startsWith('<') ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg)}` : qrSvg}
              alt="QR Code MFA"
              width={224}
              height={224}
              draggable={false}
            />
          </div>

          <div className="bg-gray-50 rounded p-2">
            <p className="text-xs text-gray-500 mb-1">Ou entrez le code manuellement :</p>
            <p className="font-mono text-xs break-all select-all text-gray-700">{secret}</p>
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Code de vérification</p>
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              placeholder="000000"
              className="text-center font-mono text-xl tracking-widest"
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
              size="sm"
              className="flex-1"
            >
              {loading ? 'Vérification...' : 'Valider'}
            </Button>
            <Button
              onClick={() => { setStep('idle'); setCode(''); setError(''); }}
              variant="outline"
              size="sm"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* ── Étape : actif ── */}
      {step === 'active' && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            Votre compte est protégé par une application d'authentification.
          </p>
          <Button
            onClick={handleDisable}
            disabled={loading}
            variant="outline"
            size="sm"
            className="w-full text-red-600 hover:bg-red-50 border-red-200"
          >
            <ShieldOff className="w-4 h-4 mr-2" />
            {loading ? 'Désactivation...' : 'Désactiver le MFA'}
          </Button>
        </div>
      )}
    </div>
  );
}
